import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useResume } from '@/contexts/resume-context';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Star, Users, Building, Upload, FileUp, Loader2 } from 'lucide-react';
import { parseResume } from '@/lib/resume-parser';

interface HeroMessage {
  type: string;
  title: string;
  subtitle: string;
  gradient: string;
  cta: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface HeroSectionProps {
  messages: HeroMessage[];
  currentMessage: number;
  nextMessage: number;
  isTransitioning: boolean;
  progress: number;
  userCount: number;
}

export function HeroSection({
  messages,
  currentMessage,
  nextMessage,
  isTransitioning,
  progress,
  userCount,
}: HeroSectionProps) {
  const { user } = useAuth();
  const { setPendingFile, setParsedResume } = useResume();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropAreaRef = useRef<HTMLDivElement>(null);

  const currentHero = messages[currentMessage];
  const nextHero = messages[nextMessage];

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Parse the file
      const data = await parseResume(file);

      clearInterval(interval);
      setUploadProgress(100);
      setIsUploading(false);
      setIsProcessing(true);

      // If user is not logged in, store data and redirect
      if (!user) {
        setPendingFile(file);
        setParsedResume(data);
        navigate('/register?resume=pending');
        return;
      }

      // For logged in users, process the data
      setParsedResume(data);
      navigate('/profile');

      toast({
        title: 'Success',
        description: 'File processed successfully!',
      });

    } catch (error: any) {
      console.error('Error processing file:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to process file. Please try again.',
      });
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="relative container px-4 md:px-6 flex flex-col items-center justify-center min-h-[40vh] gap-2 py-6">
      {/* Hero content with fixed height container */}
      <div className="relative h-32 md:h-40 lg:h-48 w-full text-center">
        {/* Current Message */}
        <div 
          className={`transition-all duration-800 ease-in-out absolute inset-0 flex flex-col items-center justify-center ${
            isTransitioning ? 'opacity-0 -translate-y-8' : 'opacity-100 translate-y-0'
          }`}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-4">
            <span className={`bg-clip-text text-transparent bg-gradient-to-r ${currentHero.gradient} dark:from-white dark:to-white/70`}>
              {currentHero.title}
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-[600px] mx-auto">
            {currentHero.subtitle}
          </p>
        </div>

        {/* Next Message */}
        <div 
          className={`transition-all duration-800 ease-in-out absolute inset-0 flex flex-col items-center justify-center ${
            isTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          aria-hidden="true"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-4">
            <span className={`bg-clip-text text-transparent bg-gradient-to-r ${nextHero.gradient} dark:from-white dark:to-white/70`}>
              {nextHero.title}
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-[600px] mx-auto">
            {nextHero.subtitle}
          </p>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="w-full max-w-2xl mx-auto mt-8 mb-8">
        <div 
          ref={dropAreaRef}
          className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-all
            ${isDragging ? 'border-primary bg-primary/5' : 'border-primary/30 hover:border-primary/50'}
            ${isUploading || isProcessing ? 'bg-primary/5 border-primary/50' : ''}
          `}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            if (!e.relatedTarget || !dropAreaRef.current?.contains(e.relatedTarget as Node)) {
              setIsDragging(false);
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <div className="py-3">
              <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin mb-2" />
              <h3 className="text-lg font-bold mb-2">Uploading your file...</h3>
              <Progress value={uploadProgress} className="w-3/4 mx-auto h-2 mb-2" />
              <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
            </div>
          ) : isProcessing ? (
            <div className="py-3">
              <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin mb-2" />
              <h3 className="text-lg font-bold mb-2">Analyzing with AI</h3>
              <p className="text-sm text-muted-foreground mb-2">This will take just a moment...</p>
              <div className="flex justify-center space-x-1">
                <span className="animate-bounce delay-0 h-2 w-2 bg-primary rounded-full"></span>
                <span className="animate-bounce delay-150 h-2 w-2 bg-primary rounded-full"></span>
                <span className="animate-bounce delay-300 h-2 w-2 bg-primary rounded-full"></span>
              </div>
            </div>
          ) : (
            <>
              <div className={`transition-all ${isDragging ? 'scale-110' : 'scale-100'}`}>
                <FileUp className="h-8 w-8 mx-auto text-primary/70 mb-2" />
                <h3 className="text-lg font-bold mb-1">Upload Your File</h3>
                <p className="text-sm text-muted-foreground mb-1">
                  Drag & drop your resume or job description
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  or click to browse files
                </p>
                <div className="flex flex-wrap justify-center gap-2 max-w-md mx-auto text-xs text-muted-foreground">
                  <Badge variant="outline">PDF</Badge>
                  <Badge variant="outline">DOCX</Badge>
                  <Badge variant="outline">TXT</Badge>
                  <Badge variant="outline">Max 5MB</Badge>
                </div>
              </div>
              <input 
                type="file" 
                ref={fileInputRef}
                className="hidden" 
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Stats Pills */}
      <div className="flex flex-wrap gap-3 justify-center">
        <Badge variant="secondary" className="px-3 py-1 text-sm">
          <Star className="h-3.5 w-3.5 mr-1 text-yellow-500" />
          Over 1 Million Profiles
        </Badge>
        <Badge variant="secondary" className="px-3 py-1 text-sm">
          <Users className="h-3.5 w-3.5 mr-1 text-blue-500" />
          {userCount.toLocaleString()} Verified Professionals
        </Badge>
        <Badge variant="secondary" className="px-3 py-1 text-sm">
          <Building className="h-3.5 w-3.5 mr-1 text-green-500" />
          750+ Hiring Companies
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
        <div 
          className="h-full bg-primary transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}